import { useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import type { SiteDeviceWithSpec } from '@shared/types'

// --- Shared icons (exported for reuse in DiscoveredDevicesPanel) ---

export function GlobeIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
}

export function TerminalIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>
}

export function MonitorIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
}

// --- Shared session start hook ---

interface ActiveSession {
  session_id: string
  target_ip: string
  target_port: number
  protocol: string
  relay_ws_url: string
}

export function useSessionStart(siteId: string, agentConnected: boolean) {
  const [sessions, setSessions] = useState<ActiveSession[]>([])
  const [startingId, setStartingId] = useState<string | null>(null)
  const { toast } = useToast()

  const startSession = useCallback(async (targetIp: string, targetPort: number, protocol: string, deviceId?: string) => {
    if (!agentConnected) {
      toast('Agent not connected. Install or check the site agent.', 'error')
      return null
    }

    const key = `${targetIp}:${targetPort}`
    setStartingId(key)
    try {
      const result = await api.post<{
        session_id: string; relay_ws_url: string;
        target_ip: string; target_port: number; protocol: string;
        warning?: string;
      }>(
        `/sites/${siteId}/sessions/start`,
        { target_ip: targetIp, target_port: targetPort, protocol, site_device_id: deviceId }
      )

      if (result.warning) {
        toast(`Session started with warning: ${result.warning}`, 'info')
      }

      const session: ActiveSession = {
        session_id: result.session_id,
        target_ip: result.target_ip,
        target_port: result.target_port,
        protocol: result.protocol,
        relay_ws_url: result.relay_ws_url,
      }
      setSessions(prev => [...prev, session])

      // For HTTP/TCP — open the proxy viewer in a new tab
      if (protocol === 'tcp' && result.relay_ws_url) {
        const proxyUrl = `/proxy?ws=${encodeURIComponent(result.relay_ws_url)}&ip=${encodeURIComponent(targetIp)}&port=${targetPort}&session=${result.session_id}`
        window.open(proxyUrl, `proxy-${result.session_id}`)
        toast(`Opened proxy viewer for ${targetIp}:${targetPort}`, 'success')
      } else {
        toast(`Session started — proxying to ${targetIp}:${targetPort} via relay`, 'success')
      }

      return session
    } catch {
      toast('Failed to start session', 'error')
      return null
    } finally {
      setStartingId(null)
    }
  }, [siteId, agentConnected, toast])

  const endSession = useCallback(async (sessionId: string) => {
    try {
      await api.patch(`/remote-sessions/${sessionId}`, { status: 'ended' })
      setSessions(prev => prev.filter(s => s.session_id !== sessionId))
      toast('Session ended', 'success')
    } catch {
      toast('Failed to end session', 'error')
    }
  }, [toast])

  return { sessions, startingId, startSession, endSession }
}

// --- Shared action buttons (port-aware) ---

export function DeviceActionButtons({ ip, ports, agentConnected, startingId, onOpen, onSSH, onRDP }: {
  ip: string
  ports: number[]
  agentConnected: boolean
  startingId: string | null
  onOpen: (port: number) => void
  onSSH: () => void
  onRDP: () => void
}) {
  const hasHTTP = ports.some(p => [80, 443, 8080, 8443].includes(p))
  const hasSSH = ports.includes(22)
  const hasRDP = ports.includes(3389)
  const httpPort = ports.includes(443) ? 443 : ports.includes(8443) ? 8443 : ports.includes(8080) ? 8080 : 80
  const isStarting = startingId === `${ip}:${httpPort}` || startingId === `${ip}:22` || startingId === `${ip}:3389`

  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
      {hasHTTP && (
        <Button
          size="sm" variant="secondary" icon={<GlobeIcon />}
          loading={isStarting && startingId === `${ip}:${httpPort}`}
          onClick={() => onOpen(httpPort)}
          disabled={!agentConnected}
        >
          Open
        </Button>
      )}
      {hasSSH ? (
        <Button
          size="sm" variant="ghost" icon={<TerminalIcon />}
          loading={isStarting && startingId === `${ip}:22`}
          onClick={onSSH}
          disabled={!agentConnected}
        >
          SSH
        </Button>
      ) : (
        <Button size="sm" variant="ghost" icon={<TerminalIcon />} disabled title="No SSH (port 22) detected">
          SSH
        </Button>
      )}
      {hasRDP ? (
        <Button
          size="sm" variant="ghost" icon={<MonitorIcon />}
          loading={isStarting && startingId === `${ip}:3389`}
          onClick={onRDP}
          disabled={!agentConnected}
          title="Remote Desktop"
        >
          RDP
        </Button>
      ) : (
        <Button size="sm" variant="ghost" icon={<MonitorIcon />} disabled title="No RDP (port 3389) detected">
          RDP
        </Button>
      )}
    </div>
  )
}

// --- SessionLauncher component ---

interface Props {
  siteId: string
  devices: SiteDeviceWithSpec[]
  agentConnected: boolean
}

export function SessionLauncher({ siteId, devices, agentConnected }: Props) {
  const { sessions, startingId, startSession, endSession } = useSessionStart(siteId, agentConnected)

  const devicesWithIP = devices.filter(d => d.ip_address)

  // Guess open ports for site devices (they don't have port scan data)
  const guessPortsForDevice = (device: SiteDeviceWithSpec): number[] => {
    const ports: number[] = [80]
    const cat = device.spec_category?.toLowerCase() || ''
    if (cat === 'camera' || cat === 'nvr') {
      ports.push(443, 554, 8080)
    }
    if (cat === 'server' || cat === 'workstation') {
      ports.push(22, 3389, 443)
    }
    if (cat === 'switch' || cat === 'router' || cat === 'ap') {
      ports.push(22, 443)
    }
    return ports
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Quick Access */}
      <div style={{
        background: 'var(--bg-1)', borderRadius: 'var(--r-l)',
        border: '1px solid var(--border)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>Quick Access</span>
          {!agentConnected && (
            <span style={{
              fontSize: 11, color: 'var(--text-3)', padding: '2px 8px',
              borderRadius: 'var(--r-f)', background: 'var(--bg-2)',
            }}>
              Agent offline
            </span>
          )}
        </div>

        {devicesWithIP.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            No devices with IP addresses found
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Device</th>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>Type</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devicesWithIP.map(device => {
                const ports = guessPortsForDevice(device)
                return (
                  <tr key={device.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>
                        {device.label || device.spec_model || 'Device'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
                      {device.ip_address}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 'var(--r-f)', fontSize: 11, fontWeight: 600,
                        background: 'var(--bg-2)', textTransform: 'capitalize',
                      }}>
                        {device.spec_category || 'other'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <DeviceActionButtons
                        ip={device.ip_address!}
                        ports={ports}
                        agentConnected={agentConnected}
                        startingId={startingId}
                        onOpen={(port) => startSession(device.ip_address!, port, 'tcp', device.id)}
                        onSSH={() => startSession(device.ip_address!, 22, 'ssh', device.id)}
                        onRDP={() => startSession(device.ip_address!, 3389, 'rdp', device.id)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Active Sessions */}
      {sessions.length > 0 && (
        <ActiveSessionsList sessions={sessions} onEnd={endSession} />
      )}
    </div>
  )
}

// --- Active sessions list (also exported for reuse) ---

export function ActiveSessionsList({ sessions, onEnd }: { sessions: ActiveSession[]; onEnd: (id: string) => void }) {
  return (
    <div style={{
      background: 'var(--bg-1)', borderRadius: 'var(--r-l)',
      border: '1px solid var(--border)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        fontSize: 14, fontWeight: 700, color: 'var(--text-1)',
      }}>
        Active Sessions ({sessions.length})
      </div>
      {sessions.map(s => (
        <div
          key={s.session_id}
          style={{
            padding: '10px 20px', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
              {s.target_ip}:{s.target_port}
            </span>
            <span style={{
              marginLeft: 8, padding: '2px 6px', borderRadius: 'var(--r-f)',
              fontSize: 10, fontWeight: 600, background: 'var(--bg-2)', textTransform: 'uppercase',
            }}>
              {s.protocol}
            </span>
          </div>
          <Button size="sm" variant="danger" onClick={() => onEnd(s.session_id)}>End</Button>
        </div>
      ))}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 16px', fontSize: 11,
  fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
}
